import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Typography, Space, Select,
  Input, Button, Badge, Alert, Tooltip, Progress, Spin, Empty,
} from 'antd';
import {
  LoginOutlined, UserOutlined, WarningOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ReloadOutlined, SearchOutlined, ClockCircleOutlined,
  SecurityScanOutlined, BarChartOutlined, FireOutlined,
} from '@ant-design/icons';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

const ROLE_LABELS = { admin: '管理員', manager: '管理者', customer: '客戶' };
const ROLE_COLORS = { admin: 'red', manager: 'blue', customer: 'green' };

function MiniBar({ value, max, color = '#1677ff' }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden',
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .3s' }} />
      </div>
      <Text style={{ fontSize: 12, minWidth: 28, textAlign: 'right' }}>{value}</Text>
    </div>
  );
}

function HourlyHeatmap({ data }) {
  if (!data?.length) return null;
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const getColor = (c) => {
    if (!c) return '#f5f5f5';
    const pct = c / maxCount;
    if (pct > 0.75) return '#003a8c';
    if (pct > 0.5)  return '#1677ff';
    if (pct > 0.25) return '#69b1ff';
    return '#bae0ff';
  };
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {data.map(d => (
        <Tooltip key={d.hour} title={`${d.hour}:00 — ${d.count} 次`}>
          <div style={{
            width: 28, height: 28, borderRadius: 4,
            background: getColor(d.count),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'default',
          }}>
            <Text style={{ fontSize: 9, color: d.count / maxCount > 0.5 ? '#fff' : '#888' }}>
              {d.hour}
            </Text>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

function DailyChart({ data }) {
  if (!data?.length) return <Empty description="暫無資料" />;
  const maxVal = Math.max(...data.map(d => d.success + d.failed), 1);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, minWidth: data.length * 22, height: 80 }}>
        {data.map(d => {
          const total = d.success + d.failed;
          const heightPx = Math.max(4, Math.round((total / maxVal) * 72));
          const failH = total ? Math.round((d.failed / total) * heightPx) : 0;
          return (
            <Tooltip key={d.date} title={
              <div>
                <div>{d.date}</div>
                <div style={{ color: '#52c41a' }}>成功：{d.success}</div>
                <div style={{ color: '#ff4d4f' }}>失敗：{d.failed}</div>
              </div>
            }>
              <div style={{ display: 'flex', flexDirection: 'column', width: 18, height: heightPx, cursor: 'default' }}>
                {failH > 0 && <div style={{ height: failH, background: '#ff7875', borderRadius: '2px 2px 0 0' }} />}
                <div style={{ flex: 1, background: '#52c41a', borderRadius: failH ? '0' : '2px 2px 0 0' }} />
              </div>
            </Tooltip>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        <span style={{ fontSize: 11, color: '#888' }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: '#52c41a', borderRadius: 2, marginRight: 4 }} />成功
        </span>
        <span style={{ fontSize: 11, color: '#888' }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: '#ff7875', borderRadius: 2, marginRight: 4 }} />失敗
        </span>
      </div>
    </div>
  );
}

function parseUA(ua = '') {
  if (!ua) return '未知';
  if (/iPhone|iPad|iPod/i.test(ua)) return '📱 iOS';
  if (/Android/i.test(ua)) return '📱 Android';
  if (/Windows/i.test(ua)) return '🖥️ Windows';
  if (/Mac/i.test(ua)) return '🍎 macOS';
  if (/Linux/i.test(ua)) return '🐧 Linux';
  return '❓ 其他';
}

function parseBrowser(ua = '') {
  if (!ua) return '未知';
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/Chrome/i.test(ua)) return 'Chrome';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
  if (/curl/i.test(ua)) return 'curl';
  return '其他';
}

export default function LoginAnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [filterSuccess, setFilterSuccess] = useState(undefined);
  const [searchUsername, setSearchUsername] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.getLoginStats(days);
      setStats(res.data ?? res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = { limit: 300 };
      if (filterSuccess !== undefined) params.success = filterSuccess;
      if (searchUsername.trim()) params.username = searchUsername.trim();
      const res = await authAPI.getLoginLogs(params);
      setLogs(res.data ?? res);
    } catch (e) {
      console.error(e);
    }
  }, [filterSuccess, searchUsername]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => fetchLogs();

  const logColumns = [
    {
      title: '時間', dataIndex: 'created_at', width: 160,
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '-',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
    },
    {
      title: '狀態', dataIndex: 'success', width: 80, align: 'center',
      render: s => s
        ? <Tag color="success" icon={<CheckCircleOutlined />}>成功</Tag>
        : <Tag color="error" icon={<CloseCircleOutlined />}>失敗</Tag>,
    },
    {
      title: '帳號', dataIndex: 'username', width: 140,
      render: (u, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.display_name || u}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{u}</Text>
        </div>
      ),
    },
    {
      title: '角色', dataIndex: 'role', width: 90,
      render: r => r ? <Tag color={ROLE_COLORS[r]}>{ROLE_LABELS[r] || r}</Tag> : '—',
    },
    {
      title: 'IP 位址', dataIndex: 'ip_address', width: 130,
      render: ip => <Text code style={{ fontSize: 12 }}>{ip || '-'}</Text>,
    },
    {
      title: '裝置 / 瀏覽器', dataIndex: 'user_agent',
      render: ua => (
        <Tooltip title={ua} placement="topLeft">
          <Space size={4}>
            <Text style={{ fontSize: 12 }}>{parseUA(ua)}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{parseBrowser(ua)}</Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: '失敗原因', dataIndex: 'failure_reason',
      render: r => r ? <Text type="danger" style={{ fontSize: 12 }}>{r}</Text> : '—',
    },
  ];

  const topUsersMaxTotal = stats?.top_users?.[0]?.total || 1;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <LoginOutlined /> 登入記錄分析
          </Title>
          <Text type="secondary">追蹤用戶登入行為，用於安全監控與行銷分析</Text>
        </div>
        <Space>
          <Select value={days} onChange={v => setDays(v)} style={{ width: 120 }}>
            <Select.Option value={7}>最近 7 天</Select.Option>
            <Select.Option value={30}>最近 30 天</Select.Option>
            <Select.Option value={90}>最近 90 天</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchLogs(); }} loading={loading}>
            重新整理
          </Button>
        </Space>
      </div>

      {loading && !stats ? <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: 80 }} /> : !stats ? null : (
        <>
          {/* KPI Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="總登入次數"
                  value={stats.total}
                  prefix={<LoginOutlined />}
                  valueStyle={{ color: '#1677ff' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>過去 {stats.period_days} 天</Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="今日登入"
                  value={stats.today_total}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  成功 {stats.today_success} / 失敗 {stats.today_total - stats.today_success}
                </Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="不重複活躍用戶"
                  value={stats.unique_users}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>登入成功的用戶數</Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="登入成功率"
                  value={stats.success_rate}
                  suffix="%"
                  prefix={<SecurityScanOutlined />}
                  valueStyle={{ color: stats.success_rate >= 90 ? '#52c41a' : stats.success_rate >= 70 ? '#faad14' : '#ff4d4f' }}
                />
                <Progress
                  percent={stats.success_rate}
                  showInfo={false}
                  size="small"
                  strokeColor={stats.success_rate >= 90 ? '#52c41a' : stats.success_rate >= 70 ? '#faad14' : '#ff4d4f'}
                  style={{ marginTop: 4 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Security Alert */}
          {stats.suspicious_ips?.length > 0 && (
            <Alert
              type="error"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
              message={`偵測到 ${stats.suspicious_ips.length} 個可疑 IP（≥5 次失敗）`}
              description={
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {stats.suspicious_ips.map((s, i) => (
                    <Tag key={i} color="error" icon={<FireOutlined />}>
                      {s.ip} — {s.failed_count} 次失敗
                      {s.usernames?.length ? ` (${s.usernames.slice(0, 2).join(', ')})` : ''}
                    </Tag>
                  ))}
                </div>
              }
            />
          )}

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Daily Chart */}
            <Col xs={24} lg={14}>
              <Card
                title={<><BarChartOutlined /> 每日登入趨勢（過去 {stats.period_days} 天）</>}
                size="small"
              >
                <DailyChart data={stats.daily} />
              </Card>
            </Col>

            {/* Hourly heatmap */}
            <Col xs={24} lg={10}>
              <Card
                title={<><ClockCircleOutlined /> 24 小時登入熱點（成功）</>}
                size="small"
              >
                <HourlyHeatmap data={stats.hourly} />
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                  顏色越深代表該小時登入次數越多（UTC 時區）
                </Text>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Top Users */}
            <Col xs={24} lg={12}>
              <Card title={<><UserOutlined /> 最活躍用戶（Top 10）</>} size="small">
                {stats.top_users?.length === 0
                  ? <Empty description="暫無資料" />
                  : stats.top_users.map((u, i) => (
                    <div key={u.username} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <Space size={4}>
                          <Badge count={i + 1} style={{ backgroundColor: i === 0 ? '#faad14' : i === 1 ? '#bfbfbf' : i === 2 ? '#d46b08' : '#d9d9d9', fontSize: 10 }} />
                          <Text style={{ fontWeight: i < 3 ? 600 : 400 }}>{u.display_name || u.username}</Text>
                          {u.role && <Tag color={ROLE_COLORS[u.role]} style={{ fontSize: 10, padding: '0 4px' }}>{ROLE_LABELS[u.role] || u.role}</Tag>}
                        </Space>
                        <Space size={4}>
                          <Text type="success" style={{ fontSize: 12 }}>✓{u.success}</Text>
                          {u.total - u.success > 0 && <Text type="danger" style={{ fontSize: 12 }}>✗{u.total - u.success}</Text>}
                        </Space>
                      </div>
                      <MiniBar value={u.total} max={topUsersMaxTotal} color={i < 3 ? '#1677ff' : '#69b1ff'} />
                    </div>
                  ))
                }
              </Card>
            </Col>

            {/* Success vs Fail breakdown */}
            <Col xs={24} lg={12}>
              <Card title={<><SecurityScanOutlined /> 安全摘要</>} size="small">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="成功登入"
                      value={stats.success}
                      valueStyle={{ color: '#52c41a', fontSize: 24 }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="失敗登入"
                      value={stats.failed}
                      valueStyle={{ color: stats.failed > 0 ? '#ff4d4f' : '#52c41a', fontSize: 24 }}
                      prefix={<CloseCircleOutlined />}
                    />
                  </Col>
                </Row>
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ fontSize: 12 }}>成功率</Text>
                    <Text strong style={{ fontSize: 12 }}>{stats.success_rate}%</Text>
                  </div>
                  <div style={{ height: 12, background: '#fff1f0', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${stats.success_rate}%`, height: '100%', background: '#52c41a', borderRadius: 6 }} />
                  </div>
                </div>
                {stats.suspicious_ips?.length > 0 && (
                  <Alert
                    type="warning" showIcon style={{ marginTop: 16 }}
                    message={`${stats.suspicious_ips.length} 個可疑 IP 需關注`}
                  />
                )}
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    統計期間：過去 {stats.period_days} 天 · 不重複活躍用戶：{stats.unique_users} 人
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Log Table */}
      <Card
        title={<><LoginOutlined /> 詳細登入記錄</>}
        size="small"
        extra={
          <Space>
            <Input
              placeholder="搜尋帳號"
              prefix={<SearchOutlined />}
              value={searchUsername}
              onChange={e => setSearchUsername(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 140 }}
              allowClear
            />
            <Select
              value={filterSuccess}
              onChange={v => setFilterSuccess(v)}
              style={{ width: 100 }}
              placeholder="全部"
              allowClear
            >
              <Select.Option value={true}>成功</Select.Option>
              <Select.Option value={false}>失敗</Select.Option>
            </Select>
            <Button size="small" icon={<SearchOutlined />} onClick={handleSearch}>搜尋</Button>
          </Space>
        }
      >
        <Table
          columns={logColumns}
          dataSource={logs}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
          scroll={{ x: 900 }}
          rowClassName={r => !r.success ? 'login-log-failed' : ''}
        />
      </Card>

      <style>{`
        .login-log-failed td { background: #fff2f0 !important; }
        .login-log-failed:hover td { background: #ffebe6 !important; }
      `}</style>
    </div>
  );
}
